import { defineStore } from 'pinia'
import api from '../services/api'
import { getDocumentTypeCode } from '../config/sectionMapping'

export const useContentStore = defineStore('content', {
  state: () => ({
    documentTypes: [],
    documentTypesMap: {}, // Map de código -> tipo de documento
    documentsCache: {}, // Cache de documentos por sección
    loading: false,
    error: null
  }),
  
  actions: {
    // Inicializar tipos de documento
    async initializeDocumentTypes() {
      if (this.documentTypes.length > 0) return
      
      try {
        this.loading = true
        const response = await api.getDocumentTypes(true)
        
        if (response.success) {
          this.documentTypes = response.data
          
          // Crear mapa para acceso rápido
          this.documentTypesMap = {}
          response.data.forEach(type => {
            this.documentTypesMap[type.codigo] = type
          })
        }
      } catch (error) {
        console.error('Error cargando tipos de documento:', error)
        this.error = error.message
      } finally {
        this.loading = false
      }
    },
    
    // Obtener tipo de documento por código
    async getDocumentTypeByCode(code) {
      // Si ya está en el mapa, devolverlo
      if (this.documentTypesMap[code]) {
        return this.documentTypesMap[code]
      }
      
      // Si no, intentar cargarlo desde el backend
      try {
        const response = await api.getDocumentTypeByCode(code)
        if (response.success) {
          this.documentTypesMap[code] = response.data
          return response.data
        }
      } catch (error) {
        console.error(`Error obteniendo tipo de documento ${code}:`, error)
        return null
      }
    },
    
    // Get content for a specific section (no usado actualmente, pero mantenido por compatibilidad)
    async getSectionContent(sectionId) {
      // Por ahora retornamos contenido vacío ya que el backend no maneja contenido HTML
      // Solo maneja documentos
      return { content: '' }
    },
    
    // Get documents for a specific section
    async getSectionDocuments(sectionId) {
      try {
        this.loading = true
        
        // Obtener el código del tipo de documento para esta sección
        const typeCode = getDocumentTypeCode(sectionId)
        
        if (!typeCode) {
          console.warn(`No se encontró mapeo para la sección: ${sectionId}`)
          return []
        }
        
        // Obtener el tipo de documento
        const documentType = await this.getDocumentTypeByCode(typeCode)
        
        if (!documentType) {
          console.warn(`No se encontró tipo de documento para el código: ${typeCode}`)
          return []
        }
        
        // Obtener documentos del backend
        const response = await api.getDocumentsByType(documentType.id)
        
        if (response.success) {
          // Transformar los documentos al formato esperado por el frontend
          const documents = response.data.map(doc => ({
              id: doc.id,
              name: doc.nombreArchivo,
              url: api.getDocumentDownloadUrl(doc.id),
              type: doc.mimeType,
              size: doc.tamanioBytes,
              uploadedAt: doc.fechaCarga,
              version: doc.version,
              comentario: doc.comentario,
              usuarioNombre: doc.usuarioNombre
          }))
          
          // Guardar en cache
          this.documentsCache[sectionId] = documents
          
          return documents
        }
        
        return []
      } catch (error) {
        console.error(`Error obteniendo documentos para sección ${sectionId}:`, error)
        this.error = error.message
        return []
      } finally {
        this.loading = false
      }
    },
    
    // Update content for a specific section (no implementado en backend)
    async updateSectionContent(sectionId, content) {
      // El backend no maneja contenido HTML, solo documentos
      // Esta función se mantiene por compatibilidad pero no hace nada
      console.warn('updateSectionContent no está implementado en el backend')
      return true
    },
    
    // Upload a document for a specific section
    async uploadDocument(sectionId, file, comentario = '') {
      try {
        this.loading = true
        
        // Obtener el código del tipo de documento para esta sección
        const typeCode = getDocumentTypeCode(sectionId)
        
        if (!typeCode) {
          throw new Error(`No se encontró mapeo para la sección: ${sectionId}`)
        }
        
        // Obtener el tipo de documento
        const documentType = await this.getDocumentTypeByCode(typeCode)
        
        if (!documentType) {
          throw new Error(`No se encontró tipo de documento para el código: ${typeCode}`)
        }
        
        // Subir el documento al backend
        const response = await api.uploadDocument(file, documentType.id, comentario)
        
        if (response.success) {
          // Limpiar cache para forzar recarga
          delete this.documentsCache[sectionId]
          
          return response.data
        } else {
          throw new Error(response.message || 'Error al subir el documento')
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Error al subir el documento'
        console.error('Error subiendo documento:', message)
        this.error = message
        throw new Error(message)
      } finally {
        this.loading = false
      }
    },
    
    // Delete a document
    async deleteDocument(documentId) {
      try {
        this.loading = true
        
        const response = await api.deleteDocument(documentId)
        
        if (response.success) {
          // Limpiar cache
          this.documentsCache = {}
          return true
        } else {
          throw new Error(response.message || 'Error al eliminar el documento')
        }
      } catch (error) {
        console.error('Error eliminando documento:', error)
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },
    
    // Crear tipo de documento (solo para admin)
    async createDocumentType(typeData) {
      try {
        this.loading = true
        
        const response = await api.createDocumentType(typeData)
        
        if (response.success) {
          // Agregar al estado local
          this.documentTypes.push(response.data)
          this.documentTypesMap[response.data.codigo] = response.data
          
          return response.data
        } else {
          throw new Error(response.message || 'Error al crear tipo de documento')
        }
      } catch (error) {
        console.error('Error creando tipo de documento:', error)
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    }
  }
})
